import { type FC, useState } from 'react';
import { DatePicker, Modal, Select, Space, Textarea } from '@g2u/ui-kit';
import { Form } from 'antd';
import { format } from 'date-fns';

import { validateFormSilently } from 'helpers';
import { BillingPlan } from 'types';

type AddServicePayload = {
  plan_ID: number;
  email_invoice: 0 | 1;
  start_date: string;
  comments?: string;
};

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onOk: (payload: AddServicePayload) => void;
  plans?: BillingPlan[];
  isSaving?: boolean;
};

type FormFields = {
  discountType: string;
  plan: number;
  emailInvoice: string;
  startingDate: Date;
  comments?: string;
};

export const AddServiceModal: FC<Props> = ({ isOpen, onCancel, onOk, plans, isSaving }) => {
  const [form] = Form.useForm<FormFields>();
  const [isFormValid, setIsFormValid] = useState(false);

  const handleFormChange = () => {
    validateFormSilently(form, setIsFormValid);
  };

  const handleFinish = (values: FormFields) => {
    onOk({
      plan_ID: values.plan,
      email_invoice: Number(values.emailInvoice) as 0 | 1,
      start_date: format(values.startingDate, 'yyyy-MM-dd'),
      comments: values.comments,
    });
  };

  return (
    <Modal.$
      open={isOpen}
      title="Add Subscription"
      okText="Save"
      cancelText="Cancel"
      onOk={() => form.submit()}
      onCancel={onCancel}
      okButtonProps={{ disabled: !isFormValid, loading: isSaving }}
      dataTestId="add-service-modal"
    >
      <Form form={form} layout="vertical" preserve={false} onValuesChange={handleFormChange} onFinish={handleFinish}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
          <Select.$
            label="Subscription"
            name="discountType"
            disabled
            initialValue="GeekCover"
            options={[{ label: 'GeekCover', value: 'GeekCover' }]}
          />
          <Select.$
            label="Plan"
            name="plan"
            required
            rules={[{ required: true, message: 'Please select a plan!' }]}
            placeholder="Please select..."
            options={plans?.map((plan) => ({ label: plan.description, value: plan.ID }))}
          />
          <Select.$
            label="Email Invoice"
            name="emailInvoice"
            initialValue="1"
            options={[
              { label: 'Yes', value: '1' },
              { label: 'No', value: '0' },
            ]}
          />
          <DatePicker.$
            label="Starting Date"
            name="startingDate"
            required
            rules={[{ required: true, message: 'Please select a starting date!' }]}
            allowClear={false}
          />
          <Textarea.$ label="Comments" name="comments" />
        </Space.$>
      </Form>
    </Modal.$>
  );
};
